import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  Layout,
  FormFieldInputNew,
  Checkbox,
  ThreeDotLoader,
  Notification,
} from "@netapp/bxp-design-system-react";
import { useTranslation } from "react-i18next";
import "./Subscribe.scss";
import OtpModal from "./OtpModal";

const SubscribeModal = ({
  advisoryId,
  open,
  setOpen,
  headerText,
  notification,
  onSubscriptionSuccess,
  setNotification,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sendUpdates, setSendUpdates] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);

  const [captchaToken, setCaptchaToken] = useState("");
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaWidgetIdRef = useRef(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Mobile detection for responsive reCAPTCHA and layout
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Load Google reCAPTCHA script once
  useEffect(() => {
    const scriptId = "recaptcha-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaLoaded(true);
      script.onerror = () => console.error("Failed to load reCAPTCHA script");
      document.head.appendChild(script);
    } else {
      setRecaptchaLoaded(true);
    }
  }, []);

  // Render reCAPTCHA explicitly and track widget id to avoid duplicate renders.
  useEffect(() => {
    if (!recaptchaLoaded || !open || !window.grecaptcha) return;
    if (!recaptchaSiteKey) {
      console.error("Missing REACT_APP_RECAPTCHA_SITE_KEY env variable");
      return;
    }

    window.grecaptcha.ready(() => {
      try {
        const container = document.getElementById("recaptcha-container");
        if (!container) return;

        if (recaptchaWidgetIdRef.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetIdRef.current);
          return;
        }

        recaptchaWidgetIdRef.current = window.grecaptcha.render(container, {
          sitekey: recaptchaSiteKey,
          callback: (token) => {
            setCaptchaToken(token);
            setIsCaptchaVerified(true);
            // remove any previous inline error
            const errorDiv = document.querySelector(".captcha-div1");
            if (errorDiv) errorDiv.innerHTML = "";
          },
          theme: "light",
          size: isMobile ? "compact" : "normal",
        });
      } catch (error) {
        console.error("Error rendering reCAPTCHA:", error);
      }
    });
  }, [recaptchaLoaded, open, isMobile, recaptchaSiteKey]);

  // Reset captcha state when modal closes
  useEffect(() => {
    if (!open) {
      if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetIdRef.current);
        } catch (_) {
          // noop
        }
      }
      recaptchaWidgetIdRef.current = null;
      setCaptchaToken("");
      setIsCaptchaVerified(false);
    }
  }, [open]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showCaptchaError = () => {
    const captchaDiv = document.querySelector(".captcha-div1");
    if (captchaDiv) {
      captchaDiv.innerHTML =
        `<span class="luci-form__field-error" role="alert">${t("modal_verifyRecaptcha.other")}</span>`;
    }
    setIsCaptchaVerified(false);
  };

  const subscribeToAdvisory = async (advId, email, captchaToken) => {
    const url = `${process.env.REACT_APP_API_BASE_URL}/subscribe`;
    // Backend Subscriber model expects `captcha_token`; the server
    // verifies it against Google's siteverify API.
    const body = { adv_id: advId, email, captcha_token: captchaToken };

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = new Error("Network response was not ok");
      err.status = response.status;
      throw err;
    }
    return response.json();
  };

  const handleSubscribe = async () => {
    if (!isValidEmail(email)) {
      alert(t("modal_enterValidEmail.other"));
      return;
    }
    // Client-side gate only: ensure the user actually solved the widget.
    // The real verification happens server-side inside /subscribe so the
    // single-use token isn't burned by a pre-flight call.
    if (!captchaToken) {
      showCaptchaError();
      return;
    }
    setIsLoading(true);
    try {
      const response = await subscribeToAdvisory(advisoryId, email, captchaToken);
      console.log("Subscription successful:", response);

      setOpen(false);
      onSubscriptionSuccess && onSubscriptionSuccess(email);
      setNotification({
        visible: true,
        message: t("modal_checkEmailForOtp.other"),
        type: "success",
      });
      setOtpModalOpen(true);
    } catch (error) {
      console.error("Subscription failed:", error);
      // 400 from /subscribe means the backend rejected the captcha; show
      // the inline captcha error rather than a generic failure toast.
      if (error && error.status === 400) {
        showCaptchaError();
        // Reset the widget so the user can solve a fresh challenge.
        if (window.grecaptcha) {
          try { window.grecaptcha.reset(); } catch (_) { /* noop */ }
        }
        setCaptchaToken("");
        setNotification({
          visible: true,
          message: t("modal_verifyRecaptcha.other"),
          type: "error",
        });
      } else {
        setNotification({
          visible: true,
          message: t("modal_subscriptionFailed.other"),
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setNotification({ visible: false, message: "", type: "success" });
      }, 4000);
    }
  };

  return (
    <>
      {open && (
        <Modal className="subscribe-modal">
          <ModalHeader>
            <div className="modal-header-title">
              {headerText || t("modal_subscribeToUpdates.other", { id: advisoryId })}
            </div>
          </ModalHeader>

          {/* Make content scrollable if viewport is short */}
          <ModalContent className="modal-content">
            {notification.visible && (
              <Notification type={notification.type}>
                {notification.message}
              </Notification>
            )}

            <Layout.Grid>
              <Layout.GridItem lg={12}>
                <FormFieldInputNew
                  className="form-field-input-new"
                  label={t("modal_email.other")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Layout.GridItem>

              <Layout.GridItem lg={12}>
                <div id="recaptcha-container" className="g-recaptcha" />
                <div className="captcha-div1" />
              </Layout.GridItem>

              <Layout.GridItem lg={12}>
                <Checkbox
                  className="checkbox"
                  checked={sendUpdates}
                  onChange={(e) => setSendUpdates(e.target.checked)}
                >
                  {t("modal_sendEmailsCheckbox.other")}
                </Checkbox>
              </Layout.GridItem>

              <Layout.GridItem lg={12} style={{ marginBottom: 16 }}>
                <Checkbox
                  className="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                >
                  {t("modal_privacyAgreementCheckbox.other")}
                </Checkbox>
              </Layout.GridItem>
            </Layout.Grid>
          </ModalContent>

          <ModalFooter className="subscribe-modal-footer">
            <Button color="secondary" onClick={() => setOpen(false)}>
              {t("modal_cancel.other")}
            </Button>
            <Button
              onClick={() => {
                setIsLoading(true);
                handleSubscribe();
              }}
              disabled={!agreed || !email || !isCaptchaVerified || isLoading}
            >
              {isLoading ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ThreeDotLoader />
                  <span style={{ marginLeft: 8 }}>{t("modal_subscribe.other")}</span>
                </div>
              ) : (
                t("modal_subscribe.other")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {otpModalOpen && (
        <OtpModal
          email={email}
          advisoryId={advisoryId}
          onClose={() => setOtpModalOpen(false)}
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
};

export default SubscribeModal;